(function () {
  'use strict';

  function trackEvent(name, detail) {
    detail = detail || {};
    if (window.dataLayer) {
      window.dataLayer.push(Object.assign({ event: name }, detail));
    }
    if (window.console && console.debug) {
      console.debug('[ShoeDNA]', name, detail);
    }
  }

  var METHOD_LABELS = {
    outfit: 'Mi Outfit Favorito',
    instagram: 'Feed de Instagram',
    pinterest: 'Tablero de Pinterest'
  };

  function isLikelyImage(file) {
    if (file.type && file.type.indexOf('image/') === 0) return true;
    // Some mobile browsers (notably iOS Safari with HEIC photos) omit the MIME type.
    // Fall back to checking the file extension so real photos aren't silently dropped.
    if (!file.type) return /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(file.name || '');
    return false;
  }

  // Claude's API rejects any image over 10MB once base64-encoded. Full-resolution
  // photos straight off a modern phone camera (12-48MP) routinely blow past that,
  // even though desktop-sourced images (screenshots, downloads) rarely do — which
  // is why this only ever showed up on mobile. Every photo is re-encoded through
  // a canvas and capped at MAX_DIMENSION so the upload always stays well under
  // the limit, regardless of what the original file looked like.
  var MAX_DIMENSION = 1600;
  var JPEG_QUALITY = 0.85;

  function convertToJpegIfNeeded(file) {
    return new Promise(function (resolve) {
      var settled = false;
      var done = function (result) {
        if (settled) return;
        settled = true;
        resolve(result);
      };

      // Safety net: never let a slow/stuck decode leave the UI hanging forever.
      var timeoutId = setTimeout(function () { done(file); }, 8000);

      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        clearTimeout(timeoutId);
        var scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
        var w = Math.round(img.naturalWidth * scale);
        var h = Math.round(img.naturalHeight * scale);
        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(function (blob) {
          done(blob ? new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }) : file);
        }, 'image/jpeg', JPEG_QUALITY);
      };
      img.onerror = function () {
        clearTimeout(timeoutId);
        URL.revokeObjectURL(url);
        done(file);
      };
      img.src = url;
    });
  }

  function ShoeDnaExperience(root) {
    this.root = root;
    this.state = { method: null, files: [], answers: {} };

    this.landingScreen = root.querySelector('[data-screen="landing"]');
    this.methodScreen = root.querySelector('[data-screen="method"]');
    this.startButton = root.querySelector('[data-start-button]');
    this.backButton = root.querySelector('[data-back-button]');
    this.methodGrid = root.querySelector('[data-method-grid]');
    this.methodCards = Array.prototype.slice.call(root.querySelectorAll('[data-method-card]'));
    this.uploadZone = root.querySelector('[data-upload-zone]');
    this.uploadSummaryText = root.querySelector('[data-upload-summary-text]');
    this.changeMethodButton = root.querySelector('[data-change-method]');
    this.dropzone = root.querySelector('[data-dropzone]');
    this.fileInput = root.querySelector('[data-file-input]');
    this.fileCountHint = root.querySelector('[data-file-count-hint]');
    this.debugStatus = root.querySelector('[data-debug-status]');
    this.previewsContainer = root.querySelector('[data-previews]');
    this.continueButton = root.querySelector('[data-continue-button]');

    this.backButtonQuestions = root.querySelector('[data-back-button-questions]');
    this.chipGroups = Array.prototype.slice.call(root.querySelectorAll('[data-chip-group]'));
    this.questionSelects = Array.prototype.slice.call(root.querySelectorAll('select[data-field]'));
    this.questionsContinueButton = root.querySelector('[data-questions-continue]');

    this.backButtonConsent = root.querySelector('[data-back-button-consent]');
    this.consentCheckbox = root.querySelector('[data-consent-checkbox]');
    this.consentContinueButton = root.querySelector('[data-consent-continue]');

    this.analysisMessages = Array.prototype.slice.call(root.querySelectorAll('[data-analysis-message]'));
    this.analysisError = root.querySelector('[data-analysis-error]');
    this.analysisErrorDetail = root.querySelector('[data-analysis-error-detail]');
    this.workerUrl = root.getAttribute('data-worker-url');

    this.revealTv = root.querySelector('[data-reveal-tv]');
    this.revealPhoto = root.querySelector('[data-reveal-photo]');
    this.revealMatchName = root.querySelector('[data-reveal-match-name]');
    this.revealMatchScore = root.querySelector('[data-reveal-match-score]');
    this.revealMatchLink = root.querySelector('[data-reveal-match-link]');
    this.revealBuyLink = root.querySelector('[data-reveal-buy-link]');
    this.revealBuyName = root.querySelector('[data-reveal-buy-name]');
    this.revealIdentity = root.querySelector('[data-reveal-identity]');
    this.revealQuote = root.querySelector('[data-reveal-quote]');
    this.revealLetter = root.querySelector('[data-reveal-letter]');
    this.revealTraits = root.querySelector('[data-reveal-traits]');
    this.revealSurprise = root.querySelector('[data-reveal-surprise]');
    this.revealContinueButton = root.querySelector('[data-reveal-continue]');

    this.shareCanvas = root.querySelector('[data-share-canvas]');
    this.shareProductLink = root.querySelector('[data-share-product-link]');
    this.shareProductName = root.querySelector('[data-share-product-name]');
    this.shareButton = root.querySelector('[data-share-button]');
    this.shareReward = root.querySelector('[data-share-reward]');
    this.shareCode = root.querySelector('[data-share-code]');
    this.copyCodeButton = root.querySelector('[data-copy-code]');
    this.shareContinueButton = root.querySelector('[data-share-continue]');
    this.shareUrl = root.getAttribute('data-share-url');
    this.shareTemplateUrl = root.getAttribute('data-share-template-url');

    this.emailForm = root.querySelector('[data-email-form]');
    this.emailInput = root.querySelector('[data-email-input]');
    this.emailSubmitButton = root.querySelector('[data-email-submit]');
    this.emailError = root.querySelector('[data-email-error]');
    this.emailErrorDetail = root.querySelector('[data-email-error-detail]');

    this.finalIdentityTab = root.querySelector('[data-final-identity-tab]');
    this.finalStamp = root.querySelector('[data-final-stamp]');
    this.finalPhoto = root.querySelector('[data-final-photo]');
    this.finalIdentity = root.querySelector('[data-final-identity]');
    this.finalMatch = root.querySelector('[data-final-match]');

    this.bindLanding();
    this.bindMethodCards();
    this.bindUpload();
    this.bindQuestions();
    this.bindConsent();
    this.bindReveal();
    this.bindShare();
    this.bindEmail();
  }

  ShoeDnaExperience.prototype.goToScreen = function (screenName) {
    var screens = this.root.querySelectorAll('.dna-screen');
    var targetScreen = null;
    screens.forEach(function (screen) {
      var isTarget = screen.getAttribute('data-screen') === screenName;
      if (isTarget) {
        targetScreen = screen;
        screen.classList.remove('is-leaving');
        screen.classList.add('is-active');
        screen.setAttribute('aria-hidden', 'false');
      } else if (screen.classList.contains('is-active')) {
        screen.classList.remove('is-active');
        screen.classList.add('is-leaving');
        screen.setAttribute('aria-hidden', 'true');

        // Once the fade-out finishes, drop it out of layout flow entirely —
        // otherwise it keeps its full height (just invisible) and pushes
        // everything below it down the page.
        screen.addEventListener('animationend', function handler() {
          screen.removeEventListener('animationend', handler);
          screen.classList.remove('is-leaving');
        }, { once: true });
      }
    });

    if (targetScreen) {
      // Scroll to the root container's top, not the target screen's own position.
      // The target screen's position shifts mid-transition while the previous
      // screen is still fading out and hasn't collapsed out of layout yet, so
      // scrolling to it directly is a race that can land far past where it
      // ends up once the collapse finishes. The root container's top offset
      // never moves during a transition, so it's a stable scroll target.
      var root = this.root;
      requestAnimationFrame(function () {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    var heading = this.root.querySelector('[data-screen="' + screenName + '"] h1, [data-screen="' + screenName + '"] h2');
    if (heading) {
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }
  };

  ShoeDnaExperience.prototype.bindLanding = function () {
    var self = this;
    if (!this.startButton) return;
    this.startButton.addEventListener('click', function () {
      trackEvent('dna_started');
      self.goToScreen('method');
    });
  };

  ShoeDnaExperience.prototype.bindMethodCards = function () {
    var self = this;

    if (this.backButton) {
      this.backButton.addEventListener('click', function () {
        self.goToScreen('landing');
      });
    }

    this.methodCards.forEach(function (card) {
      card.addEventListener('click', function () {
        self.selectMethod(card);
      });
    });

    if (this.changeMethodButton) {
      this.changeMethodButton.addEventListener('click', function () {
        self.resetMethod();
      });
    }
  };

  ShoeDnaExperience.prototype.selectMethod = function (card) {
    var method = card.getAttribute('data-method');
    var maxFiles = parseInt(card.getAttribute('data-max-files'), 10) || 1;

    this.state.method = method;
    this.state.maxFiles = maxFiles;
    this.state.files = [];

    this.methodCards.forEach(function (c) {
      var selected = c === card;
      c.classList.toggle('is-selected', selected);
      c.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    this.methodGrid.hidden = true;
    this.uploadZone.hidden = false;
    this.uploadSummaryText.textContent = METHOD_LABELS[method] || method;
    if (maxFiles > 1) {
      this.fileInput.setAttribute('multiple', 'multiple');
    } else {
      this.fileInput.removeAttribute('multiple');
    }
    this.fileCountHint.textContent = maxFiles > 1
      ? 'Hasta ' + maxFiles + ' fotos'
      : '1 foto';

    this.renderPreviews();
    this.updateContinueState();

    var uploadZone = this.uploadZone;
    requestAnimationFrame(function () {
      uploadZone.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  ShoeDnaExperience.prototype.resetMethod = function () {
    this.state.method = null;
    this.state.files = [];
    this.methodCards.forEach(function (c) {
      c.classList.remove('is-selected');
      c.setAttribute('aria-pressed', 'false');
    });
    this.methodGrid.hidden = false;
    this.uploadZone.hidden = true;
    this.fileInput.value = '';
    this.renderPreviews();
  };

  ShoeDnaExperience.prototype.bindUpload = function () {
    var self = this;

    // No click handler needed here: data-dropzone is a <label> that already
    // wraps data-file-input, so the browser opens the file picker natively
    // on click. Calling fileInput.click() manually on top of that fired the
    // native photo picker twice in near-simultaneous succession, which iOS
    // Safari's picker sheet doesn't handle cleanly — it would open and close
    // normally but never actually commit a file, so `change` never fired.

    this.dropzone.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        self.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', function () {
      self.addFiles(self.fileInput.files);
      self.fileInput.value = '';
    });

    ['dragenter', 'dragover'].forEach(function (evtName) {
      self.dropzone.addEventListener(evtName, function (e) {
        e.preventDefault();
        self.dropzone.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (evtName) {
      self.dropzone.addEventListener(evtName, function (e) {
        e.preventDefault();
        self.dropzone.classList.remove('is-dragover');
      });
    });

    this.dropzone.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) {
        self.addFiles(e.dataTransfer.files);
      }
    });

    this.continueButton.addEventListener('click', function () {
      self.handleContinue();
    });
  };

  ShoeDnaExperience.prototype.setDebugStatus = function (text) {
    if (this.debugStatus) this.debugStatus.textContent = text || '';
  };

  ShoeDnaExperience.prototype.addFiles = function (fileList) {
    var self = this;

    if (!fileList || fileList.length === 0) {
      this.setDebugStatus('No se recibió ningún archivo del selector.');
      return;
    }

    var allNames = Array.prototype.map.call(fileList, function (f) {
      return f.name + ' (' + (f.type || 'sin tipo') + ', ' + Math.round(f.size / 1024) + 'KB)';
    }).join(', ');

    var incoming = Array.prototype.filter.call(fileList, isLikelyImage);

    if (incoming.length === 0) {
      this.setDebugStatus('Recibido pero no reconocido como imagen: ' + allNames);
      return;
    }

    var remaining = this.state.maxFiles - this.state.files.length;
    if (remaining <= 0) {
      this.setDebugStatus('Ya tienes el máximo de fotos para este método.');
      return;
    }

    incoming = incoming.slice(0, remaining);
    this.setDebugStatus('Procesando: ' + allNames + '…');

    Promise.all(incoming.map(convertToJpegIfNeeded)).then(function (converted) {
      self.state.files = self.state.files.concat(converted);
      self.renderPreviews();
      self.updateContinueState();
      self.setDebugStatus('Listo — ' + self.state.files.length + ' foto(s) agregada(s).');
    }).catch(function (err) {
      self.setDebugStatus('Error procesando la foto: ' + (err && err.message ? err.message : err));
    });
  };

  ShoeDnaExperience.prototype.removeFile = function (index) {
    this.state.files.splice(index, 1);
    this.renderPreviews();
    this.updateContinueState();
  };

  ShoeDnaExperience.prototype.renderPreviews = function () {
    var self = this;
    this.previewsContainer.innerHTML = '';

    this.state.files.forEach(function (file, index) {
      var wrap = document.createElement('div');
      wrap.className = 'dna-preview';

      var img = document.createElement('img');
      img.src = URL.createObjectURL(file);
      img.alt = '';
      img.onload = function () {
        URL.revokeObjectURL(img.src);
      };

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'dna-preview__remove';
      removeBtn.setAttribute('aria-label', 'Quitar foto');
      removeBtn.textContent = '×';
      removeBtn.addEventListener('click', function () {
        self.removeFile(index);
      });

      wrap.appendChild(img);
      wrap.appendChild(removeBtn);
      self.previewsContainer.appendChild(wrap);
    });

    var atMax = this.state.files.length >= this.state.maxFiles;
    this.dropzone.style.display = atMax ? 'none' : '';
  };

  ShoeDnaExperience.prototype.updateContinueState = function () {
    var hasFiles = this.state.files.length > 0;
    this.continueButton.disabled = !hasFiles;
    if (hasFiles) {
      trackEvent('upload_completed', { method: this.state.method, file_count: this.state.files.length });
    }
  };

  ShoeDnaExperience.prototype.handleContinue = function () {
    this.root.dispatchEvent(new CustomEvent('shoedna:method-complete', {
      bubbles: true,
      detail: { method: this.state.method, files: this.state.files }
    }));
    this.goToScreen('questions');
  };

  ShoeDnaExperience.prototype.bindQuestions = function () {
    var self = this;

    if (this.backButtonQuestions) {
      this.backButtonQuestions.addEventListener('click', function () {
        self.goToScreen('method');
      });
    }

    this.questionSelects.forEach(function (select) {
      var field = select.getAttribute('data-field');
      select.addEventListener('change', function () {
        self.state.answers[field] = select.value || null;
      });
    });

    this.chipGroups.forEach(function (group) {
      var field = group.getAttribute('data-chip-group');
      var chips = Array.prototype.slice.call(group.querySelectorAll('[data-chip-value]'));
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var alreadySelected = chip.classList.contains('is-selected');
          chips.forEach(function (c) { c.classList.remove('is-selected'); });
          if (alreadySelected) {
            self.state.answers[field] = null;
          } else {
            chip.classList.add('is-selected');
            self.state.answers[field] = chip.getAttribute('data-chip-value');
          }
        });
      });
    });

    if (this.questionsContinueButton) {
      this.questionsContinueButton.addEventListener('click', function () {
        self.handleQuestionsContinue();
      });
    }
  };

  ShoeDnaExperience.prototype.handleQuestionsContinue = function () {
    trackEvent('personality_questions_completed', this.state.answers);
    this.root.dispatchEvent(new CustomEvent('shoedna:questions-complete', {
      bubbles: true,
      detail: { answers: this.state.answers }
    }));
    this.goToScreen('consent');
  };

  ShoeDnaExperience.prototype.bindConsent = function () {
    var self = this;

    if (this.backButtonConsent) {
      this.backButtonConsent.addEventListener('click', function () {
        self.goToScreen('questions');
      });
    }

    if (this.consentCheckbox) {
      this.consentCheckbox.addEventListener('change', function () {
        self.consentContinueButton.disabled = !self.consentCheckbox.checked;
      });
    }

    if (this.consentContinueButton) {
      this.consentContinueButton.addEventListener('click', function () {
        self.handleConsentContinue();
      });
    }
  };

  ShoeDnaExperience.prototype.handleConsentContinue = function () {
    if (!this.consentCheckbox.checked) return;
    trackEvent('consent_accepted');
    this.root.dispatchEvent(new CustomEvent('shoedna:consent-complete', {
      bubbles: true
    }));
    this.goToScreen('analysis');
    this.runAnalysis();
  };

  var ANALYSIS_MESSAGE_DURATION = 1250;

  ShoeDnaExperience.prototype.runAnalysis = function () {
    var self = this;
    this.analysisError.hidden = true;

    var messagesDone = this.playAnalysisMessages();
    var fetchDone = this.fetchDnaResult();

    Promise.all([messagesDone, fetchDone]).then(function (results) {
      var result = results[1];
      self.state.result = result;
      trackEvent('ai_analysis_completed', { identity: result && result.identity });
      self.root.dispatchEvent(new CustomEvent('shoedna:analysis-complete', {
        bubbles: true,
        detail: { result: result }
      }));
      self.showReveal(result);
    }).catch(function (err) {
      console.error('[ShoeDNA] analysis failed', err);
      self.analysisError.hidden = false;
      // TEMP diagnostic: surface the real error so we're not guessing blind
      // on devices we can't attach devtools to.
      if (self.analysisErrorDetail) {
        self.analysisErrorDetail.textContent = ' [DIAG] ' + (err && err.message ? err.message : String(err));
      }
    });
  };

  // Product listings often mix portrait lifestyle shots with landscape
  // product-only shots. Picking whichever image's aspect ratio is closest to
  // the display slot (targetRatio = width/height) means a cover-fit crop
  // has to cut away much less of the shoe, instead of centering on a random
  // point of a tall photo. Falls back to the suggest.json thumbnail if the
  // full product JSON can't be fetched for some reason.
  ShoeDnaExperience.prototype.fetchProductInfo = function (productName, targetRatio) {
    if (!productName) return Promise.resolve(null);
    var searchUrl = '/search/suggest.json?q=' + encodeURIComponent(productName)
      + '&resources[type]=product&resources[limit]=1&resources[options][unavailable_products]=show';

    return fetch(searchUrl)
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) {
        var products = data && data.resources && data.resources.results && data.resources.results.products;
        var product = products && products.length && products[0];
        if (!product) return null;

        var fallback = { image: product.image || null, url: product.url || null };
        if (!product.handle || !targetRatio) return fallback;

        return fetch('/products/' + product.handle + '.json')
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (productData) {
            var images = productData && productData.product && productData.product.images;
            if (!images || !images.length) return fallback;

            var best = images[0];
            var bestDiff = Infinity;
            images.forEach(function (img) {
              var ratio = img.width / img.height;
              var diff = Math.abs(ratio - targetRatio);
              if (diff < bestDiff) {
                bestDiff = diff;
                best = img;
              }
            });
            return { image: best.src, url: fallback.url };
          })
          .catch(function () { return fallback; });
      })
      .catch(function () { return null; });
  };

  ShoeDnaExperience.prototype.showReveal = function (result) {
    if (!result) return;
    var self = this;

    var topMatchName = result.top_match && result.top_match.name;
    this.fetchProductInfo(topMatchName, 1.36).then(function (info) {
      // If the AI's match name doesn't resolve to a real product image, the
      // TV screen would otherwise stay blank. Falling back to the customer's
      // own uploaded photo keeps it from ever looking broken.
      var fallbackPhoto = self.state.files[0] ? URL.createObjectURL(self.state.files[0]) : '';
      self.revealPhoto.src = (info && info.image) || fallbackPhoto;
      var productUrl = (info && info.url) || '#';
      self.revealMatchLink.href = productUrl;
      self.revealTv.href = productUrl;
      self.revealBuyLink.href = productUrl;
    });

    if (result.top_match) {
      this.revealMatchName.textContent = result.top_match.name || '';
      this.revealMatchScore.textContent = Math.round(result.top_match.score) + '% match';
      this.revealBuyName.textContent = result.top_match.name || '';
      var handleMatchClick = function () {
        trackEvent('product_clicked', { name: result.top_match.name, position: 'top_match' });
      };
      var handleBuyClick = function () {
        trackEvent('product_clicked', { name: result.top_match.name, position: 'buy_button' });
      };
      this.revealMatchLink.onclick = handleMatchClick;
      this.revealTv.onclick = handleMatchClick;
      this.revealBuyLink.onclick = handleBuyClick;
    }

    this.revealIdentity.textContent = result.identity || '';
    this.revealQuote.textContent = result.identity_quote || '';
    this.revealLetter.textContent = result.letter || '';
    this.revealSurprise.textContent = result.surprise_observation || '';

    this.revealTraits.innerHTML = '';
    var traitsContainer = this.revealTraits;
    (result.signature_traits || []).forEach(function (trait) {
      var chip = document.createElement('span');
      chip.className = 'dna-reveal__trait';
      chip.textContent = trait;
      traitsContainer.appendChild(chip);
    });

    this.goToScreen('reveal');
    trackEvent('dna_revealed', { identity: result.identity });

    var tv = this.revealTv;
    tv.classList.remove('is-revealed');
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        tv.classList.add('is-revealed');
      });
    });
  };

  ShoeDnaExperience.prototype.bindReveal = function () {
    var self = this;
    if (!this.revealContinueButton) return;
    this.revealContinueButton.addEventListener('click', function () {
      self.root.dispatchEvent(new CustomEvent('shoedna:reveal-continue', {
        bubbles: true,
        detail: { result: self.state.result }
      }));
      self.goToScreen('share');
      self.buildShareCard(self.state.result);
    });
  };

  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve) {
      if (!src) { resolve(null); return; }
      var img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = src;
    });
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function drawRoundedImageCover(ctx, img, x, y, w, h, radius) {
    if (!img) return;
    ctx.save();
    roundRectPath(ctx, x, y, w, h, radius);
    ctx.clip();

    var imgRatio = img.width / img.height;
    var boxRatio = w / h;
    var drawW, drawH, drawX, drawY;
    if (imgRatio > boxRatio) {
      drawH = h;
      drawW = h * imgRatio;
      drawX = x - (drawW - w) / 2;
      drawY = y;
    } else {
      drawW = w;
      drawH = w / imgRatio;
      drawX = x;
      drawY = y - (drawH - h) / 2;
    }
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }

  // Cover-fit crops whatever doesn't match the box's ratio, which can cut the
  // shoe off. Contain-fit shrinks the whole photo to fit inside the box
  // instead — the entire shoe always stays visible, at the cost of a sliver
  // of background showing on the shorter side.
  function drawContainImage(ctx, img, x, y, w, h, bgColor) {
    if (!img) return;
    ctx.save();
    roundRectPath(ctx, x, y, w, h, 0);
    ctx.clip();
    if (bgColor) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(x, y, w, h);
    }

    var imgRatio = img.width / img.height;
    var boxRatio = w / h;
    var drawW, drawH, drawX, drawY;
    if (imgRatio > boxRatio) {
      drawW = w;
      drawH = w / imgRatio;
      drawX = x;
      drawY = y + (h - drawH) / 2;
    } else {
      drawH = h;
      drawW = h * imgRatio;
      drawX = x + (w - drawW) / 2;
      drawY = y;
    }
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  }

  // A flat, sharp-edged photo dropped into a device "screen" reads as an
  // obviously pasted sticker. A faint inner shadow around the edges gives it
  // just enough depth to look like it's actually sitting inside the screen.
  function drawScreenVignette(ctx, x, y, w, h) {
    ctx.save();
    roundRectPath(ctx, x, y, w, h, 0);
    ctx.clip();
    var grad = ctx.createRadialGradient(
      x + w / 2, y + h / 2, Math.min(w, h) * 0.35,
      x + w / 2, y + h / 2, Math.max(w, h) * 0.72
    );
    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.22)');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
  }

  function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    var words = (text || '').split(' ');
    var line = '';
    var lines = [];
    for (var i = 0; i < words.length; i++) {
      var testLine = line + words[i] + ' ';
      if (ctx.measureText(testLine).width > maxWidth && line !== '') {
        lines.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    lines.forEach(function (l, index) {
      ctx.fillText(l, x, y + index * lineHeight);
    });
    return lines.length * lineHeight;
  }

  ShoeDnaExperience.prototype.generateQrDataUrl = function (text, size) {
    return new Promise(function (resolve) {
      if (!window.QRCode || !text) { resolve(null); return; }
      var container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      document.body.appendChild(container);
      new window.QRCode(container, {
        text: text,
        width: size,
        height: size,
        colorDark: '#29193E',
        colorLight: '#FBF7F1'
      });
      setTimeout(function () {
        var canvasEl = container.querySelector('canvas');
        var imgEl = container.querySelector('img');
        var dataUrl = canvasEl ? canvasEl.toDataURL('image/png') : (imgEl ? imgEl.src : null);
        document.body.removeChild(container);
        resolve(dataUrl);
      }, 80);
    });
  };

  // Screen regions of dna-share-template.png, expressed as fractions of the
  // template's own 1080x1350 size — found by sampling the template's pixels
  // for the grey/white placeholder rectangles inside the computer and camera.
  // Tweak these four numbers each to nudge a photo's position/size.
  var TEMPLATE_COMPUTER_SCREEN = { x: 475 / 1080, y: 105 / 1350, w: (755 - 475) / 1080, h: (345 - 105) / 1350 };
  var TEMPLATE_CAMERA_SCREEN = { x: 108 / 1080, y: 905 / 1350, w: (545 - 108) / 1080, h: (1195 - 905) / 1350 };
  // Right edge of the whole camera body (not just its screen) — the free
  // margin for the match name/score text starts just past this.
  var TEMPLATE_CAMERA_RIGHT_EDGE = 930 / 1080;

  ShoeDnaExperience.prototype.buildShareCard = function (result) {
    if (!result || !this.shareCanvas || !result.top_match) return;
    var self = this;

    var canvas = this.shareCanvas;
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    var W = canvas.width;
    var H = canvas.height;

    var customerPhotoSrc = this.state.files[0] ? URL.createObjectURL(this.state.files[0]) : null;
    var fontsReady = (document.fonts && document.fonts.ready) || Promise.resolve();

    Promise.all([
      fontsReady,
      loadImage(customerPhotoSrc, false),
      this.fetchProductInfo(result.top_match.name, 1.87).then(function (info) {
        return loadImage(info && info.image, true).then(function (img) {
          return { img: img, url: (info && info.url) || '#' };
        });
      }),
      loadImage(this.shareTemplateUrl, true),
      this.generateQrDataUrl(this.shareUrl, 300).then(function (dataUrl) {
        return loadImage(dataUrl, false);
      })
    ]).then(function (imgs) {
      var customerImg = imgs[1];
      var matchImg = imgs[2].img;
      var templateImg = imgs[3];

      if (self.shareProductLink) {
        self.shareProductLink.href = imgs[2].url;
        self.shareProductLink.onclick = function () {
          trackEvent('product_clicked', { name: result.top_match.name, position: 'buy_button' });
        };
      }
      if (self.shareProductName) {
        self.shareProductName.textContent = result.top_match.name || '';
      }
      var qrImg = imgs[4];

      var bgGradient = ctx.createLinearGradient(0, 0, 0, H);
      bgGradient.addColorStop(0, '#ECDDFE');
      bgGradient.addColorStop(0.45, '#F6EEE3');
      bgGradient.addColorStop(1, '#AEB4F4');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, W, H);

      // The template's screen areas are opaque static texture, not a real
      // cutout, so the template has to be drawn first and the photos on top
      // of it — the reverse order would just get the photos painted over.
      if (templateImg) {
        ctx.drawImage(templateImg, 0, 0, W, H);
      }

      var computerRect = TEMPLATE_COMPUTER_SCREEN;
      if (customerImg) {
        drawContainImage(
          ctx, customerImg,
          computerRect.x * W, computerRect.y * H, computerRect.w * W, computerRect.h * H,
          '#D9D4C9'
        );
        drawScreenVignette(ctx, computerRect.x * W, computerRect.y * H, computerRect.w * W, computerRect.h * H);
      }

      var cameraRect = TEMPLATE_CAMERA_SCREEN;
      if (matchImg) {
        drawContainImage(
          ctx, matchImg,
          cameraRect.x * W, cameraRect.y * H, cameraRect.w * W, cameraRect.h * H,
          '#D9D4C9'
        );
        drawScreenVignette(ctx, cameraRect.x * W, cameraRect.y * H, cameraRect.w * W, cameraRect.h * H);
      }

      // Identity name + quote, under the "MI SHOE DNA" title in the template.
      ctx.textAlign = 'left';
      ctx.fillStyle = '#D885A3';
      ctx.font = '700 40px "Space Mono"';
      var identityY = 300;
      var identityHeight = wrapText(ctx, (result.identity || '').toUpperCase(), 50, identityY, 330, 48);

      ctx.fillStyle = 'rgba(41,25,62,0.75)';
      ctx.font = 'italic 400 24px "Playfair Display"';
      var quoteY = identityY + identityHeight + 20;
      wrapText(ctx, result.identity_quote || '', 50, quoteY, 330, 32);

      // Match name + score, in the free margin past the right edge of the
      // whole camera body (not just its screen — that margin is much narrower).
      ctx.textAlign = 'left';
      ctx.fillStyle = '#29193E';
      ctx.font = '700 26px "Space Mono"';
      var matchTextX = TEMPLATE_CAMERA_RIGHT_EDGE * W + 20;
      var matchTextMaxWidth = W - matchTextX - 30;
      var matchTextY = cameraRect.y * H + cameraRect.h * H / 2 - 30;
      var matchNameHeight = wrapText(ctx, (result.top_match.name || '').toUpperCase(), matchTextX, matchTextY, matchTextMaxWidth, 32);
      ctx.fillStyle = 'rgba(41,25,62,0.7)';
      ctx.font = '600 22px "DM Sans"';
      ctx.fillText(Math.round(result.top_match.score) + '% match', matchTextX, matchTextY + matchNameHeight + 8);

      if (qrImg) {
        var qrSize = 130;
        var qrPad = 14;
        var qrX = W - 60 - qrSize;
        var qrY = H - 60 - qrSize;
        ctx.fillStyle = '#FFFFFF';
        roundRectPath(ctx, qrX - qrPad, qrY - qrPad, qrSize + qrPad * 2, qrSize + qrPad * 2, 12);
        ctx.fill();
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

        ctx.fillStyle = 'rgba(41,25,62,0.6)';
        ctx.font = '600 18px "DM Sans"';
        ctx.textAlign = 'center';
        ctx.fillText('Descubre el tuyo', qrX + qrSize / 2, qrY - qrPad - 10);
      }
    });
  };

  ShoeDnaExperience.prototype.bindShare = function () {
    var self = this;

    if (this.shareButton) {
      this.shareButton.addEventListener('click', function () {
        self.handleShareClick();
      });
    }

    if (this.copyCodeButton) {
      this.copyCodeButton.addEventListener('click', function () {
        var code = self.shareCode.textContent.trim();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(code).catch(function () {});
        }
        trackEvent('discount_claimed', { code: code });
        self.copyCodeButton.textContent = 'Copiado ✓';
        setTimeout(function () {
          self.copyCodeButton.textContent = 'Copiar';
        }, 2000);
      });
    }

    if (this.shareContinueButton) {
      this.shareContinueButton.addEventListener('click', function () {
        self.root.dispatchEvent(new CustomEvent('shoedna:share-continue', { bubbles: true }));
        self.goToScreen('email');
      });
    }
  };

  ShoeDnaExperience.prototype.handleShareClick = function () {
    var self = this;
    trackEvent('share_clicked');

    this.shareCanvas.toBlob(function (blob) {
      if (!blob) return;
      var file = new File([blob], 'your-shoe-dna.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        navigator.share({
          files: [file],
          title: 'Mi Shoe DNA',
          text: 'Acabo de descubrir mi Shoe DNA ✨'
        }).catch(function () {});
      } else {
        var link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'your-shoe-dna.png';
        link.click();
      }

      self.shareReward.hidden = false;
    }, 'image/png');
  };

  ShoeDnaExperience.prototype.bindEmail = function () {
    var self = this;
    if (!this.emailForm) return;

    this.emailForm.addEventListener('submit', function (e) {
      e.preventDefault();
      self.handleEmailSubmit();
    });
  };

  ShoeDnaExperience.prototype.handleEmailSubmit = function () {
    var self = this;
    var email = this.emailInput.value.trim();
    if (!email) return;

    this.emailError.hidden = true;
    this.emailSubmitButton.disabled = true;

    var formData = new FormData(this.emailForm);

    fetch(this.emailForm.action, { method: 'POST', body: formData })
      .then(function (response) {
        if (!response.ok) {
          return response.text().then(function (bodyText) {
            throw new Error('Contact form status ' + response.status + ': ' + bodyText.slice(0, 200));
          });
        }
        trackEvent('email_captured', { email: email });
        self.root.dispatchEvent(new CustomEvent('shoedna:email-complete', {
          bubbles: true,
          detail: { email: email }
        }));
        self.emailSubmitButton.disabled = false;
        self.emailSubmitButton.textContent = 'Guardado ✓';
        self.showFinal(self.state.result);
      })
      .catch(function (err) {
        console.error('[ShoeDNA] email capture failed', err);
        self.emailError.hidden = false;
        self.emailSubmitButton.disabled = false;
        // TEMP diagnostic: surface the real failure reason so a repeat
        // doesn't require guessing blind again.
        if (self.emailErrorDetail) {
          self.emailErrorDetail.textContent = ' [DIAG] ' + (err && err.message ? err.message : String(err));
        }
      });
  };

  ShoeDnaExperience.prototype.showFinal = function (result) {
    if (!result) return;

    this.finalIdentityTab.textContent = result.identity || 'Shoe DNA';
    this.finalIdentity.textContent = result.identity || '';
    this.finalMatch.textContent = result.top_match ? ('Match con ' + result.top_match.name) : '';

    if (this.state.files[0]) {
      this.finalPhoto.src = URL.createObjectURL(this.state.files[0]);
    }

    var today = new Date();
    this.finalStamp.textContent = 'Archivado — ' + today.toLocaleDateString('es-MX', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    this.goToScreen('final');
  };

  ShoeDnaExperience.prototype.playAnalysisMessages = function () {
    var messages = this.analysisMessages;
    messages.forEach(function (m) { m.classList.remove('is-active'); });

    return new Promise(function (resolve) {
      var index = 0;
      function showNext() {
        if (index > 0) messages[index - 1].classList.remove('is-active');
        if (index >= messages.length) {
          resolve();
          return;
        }
        messages[index].classList.add('is-active');
        index += 1;
        setTimeout(showNext, ANALYSIS_MESSAGE_DURATION);
      }
      showNext();
    });
  };

  ShoeDnaExperience.prototype.fetchDnaResult = function () {
    var formData = new FormData();
    this.state.files.forEach(function (file) {
      formData.append('image', file);
    });
    var answers = this.state.answers;
    if (answers.zodiac) formData.append('zodiac', answers.zodiac);
    if (answers.coffee) formData.append('coffee', answers.coffee);
    if (answers.season) formData.append('season', answers.season);
    if (answers.color) formData.append('color', answers.color);

    return fetch(this.workerUrl, { method: 'POST', body: formData }).then(function (response) {
      if (!response.ok) {
        return response.text().then(function (bodyText) {
          throw new Error('Worker status ' + response.status + ': ' + bodyText.slice(0, 200));
        });
      }
      return response.json();
    });
  };

  // Works around a third-party popup/notification app (fixed-position, high
  // z-index) that sits on top of our buttons and silently swallows taps/clicks
  // via an empty "phantom" modal overlay. Neutralized only on this page, only
  // while our experience is present, so the rest of the site is unaffected.
  function neutralizePhantomOverlays() {
    document.querySelectorAll('.react-responsive-modal-root').forEach(function (root) {
      var container = root.querySelector('.react-responsive-modal-container');
      var hasVisibleModal = container && container.children.length > 0;
      root.style.pointerEvents = hasVisibleModal ? '' : 'none';
    });

    // The Ecomsend/Trustoo popup script drops a stray "}" text node as a direct
    // child of <body> when it injects its widget — strip it if present.
    Array.prototype.slice.call(document.body.childNodes).forEach(function (node) {
      if (node.nodeType === 3 && node.textContent.trim() === '}') {
        node.remove();
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-shoe-dna]').forEach(function (root) {
      new ShoeDnaExperience(root);
    });

    neutralizePhantomOverlays();
    setInterval(neutralizePhantomOverlays, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
